import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faStop,
  faSearch,
  faUpload,
  faTimes,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import apiExpenseManagement from "../../services/api/apiExpenseManagement";

const PageContainer = styled.div`
  padding: 40px 24px;
  background-color: #f2f2f2;
  min-height: 100vh;
  box-sizing: border-box;
  font-size: 15px;
`;

const MainCard = styled.div`
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 32px;
  width: 100%;
  max-width: 1000px; /* Largura controlada para não espalhar demais */
  margin: 0 auto;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const PrimaryButton = styled.button`
  background-color: #ff8f40;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover { opacity: 0.9; }
  &:disabled { background-color: #ccc; cursor: not-allowed; }
`;

const IconButton = styled.button`
  background: ${props => props.danger ? "#fff1f0" : "#f5f5f5"};
  color: ${props => props.danger ? "#ff4d4f" : "#666"};
  border: 1px solid ${props => props.danger ? "#ffa39e" : "#eaeaea"};
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;

  &:hover {
    background: ${props => props.danger ? "#ff4d4f" : "#ff8f40"};
    color: #fff;
    border-color: ${props => props.danger ? "#ff4d4f" : "#ff8f40"};
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #eaeaea;
  border-radius: 6px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  background: #fff;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #eaeaea;
  color: #333;
  font-weight: 600;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #eaeaea;
  color: #666;
  vertical-align: middle;
`;

const StatusTag = styled.span`
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  display: inline-block;
  background: ${props => {
    if (props.status === 'concluido') return '#f6ffed';
    if (props.status === 'erro') return '#fff2f0';
    return '#e6f7ff';
  }};
  color: ${props => {
    if (props.status === 'concluido') return '#52c41a';
    if (props.status === 'erro') return '#ff4d4f';
    return '#1890ff';
  }};
  border: 1px solid currentColor;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 100%;
  max-width: ${props => props.width || '450px'};
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 24px;
  box-sizing: border-box;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 6px;
    color: #333;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff8f40; }
`;

const UploadArea = styled.div`
  border: 2px dashed #eaeaea;
  padding: 24px;
  border-radius: 6px;
  text-align: center;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover { border-color: #ff8f40; }

  p { margin: 8px 0 0; color: #999; font-size: 14px; }
`;

const FileList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  font-size: 14px;
  color: #555;
`;

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchFiles, setBatchFiles] = useState([]);
  const [formState, setFormState] = useState({ name: "" });
  const [fileList, setFileList] = useState([]);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const res = await apiExpenseManagement.getBatches();
      if (res.status === 200) setBatches(res.data);
    } catch (err) {
      toast.error("Erro ao buscar lotes: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const openDetailsModal = async (batch) => {
    try {
      const [detailsRes, filesRes] = await Promise.all([
        apiExpenseManagement.getBatch(batch.batch_id),
        apiExpenseManagement.getBatchFiles(batch.batch_id)
      ]);
      setBatchDetails(detailsRes.data);
      setBatchFiles(filesRes.data);
      setDetailsModalOpen(true);
    } catch (err) {
      toast.error("Erro ao buscar detalhes: " + err.message);
    }
  };

  const handleDelete = async (batchId) => {
    if (window.confirm("Deseja realmente excluir este lote?")) {
      try {
        await apiExpenseManagement.deleteBatch(batchId);
        toast.success("Lote excluído com sucesso!");
        fetchBatches();
      } catch (err) {
        toast.error("Erro ao excluir lote: " + err.message);
      }
    }
  };

  const handleCreateSubmit = async () => {
    if (!formState.name || fileList.length === 0) {
      toast.warning("Nome e arquivos são obrigatórios.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("sistema", "controle_contas");
      formData.append("lote", formState.name);
      formData.append("lote_id", "");
      fileList.forEach(file => formData.append("files[]", file));
      const res = await apiExpenseManagement.uploadMultiple(formData);
      if (res.status === 200) {
        toast.success("Lote criado!");
        setCreateModalOpen(false);
        setFileList([]);
        setFormState({ name: "" });
        fetchBatches();
      }
    } catch (err) {
      toast.error("Erro na criação: " + err.message);
    }
  };

  return (
    <PageContainer>
      <MainCard>
        <Header>
          <Title>Controle de Contas</Title>
          <PrimaryButton onClick={() => setCreateModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} />
            Novo Lote
          </PrimaryButton>
        </Header>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>Lote</Th>
                <Th>Criação</Th>
                <Th>Arquivos</Th>
                <Th>Status</Th>
                <Th style={{ textAlign: 'center' }}>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan="5" style={{ textAlign: 'center', padding: '60px' }}>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#ff8f40" />
                  </Td>
                </tr>
              ) : batches.map(batch => (
                <tr key={batch.batch_id}>
                  <Td>
                    <button
                      style={{ background: 'none', border: 'none', color: '#ff8f40', cursor: 'pointer', fontSize: '15px', fontWeight: '500', padding: 0 }}
                      onClick={() => openDetailsModal(batch)}
                    >
                      <FontAwesomeIcon icon={faSearch} style={{ marginRight: '8px' }} />
                      {batch.name}
                    </button>
                  </Td>
                  <Td>{dayjs(batch.createdat).format("DD/MM/YYYY")}</Td>
                  <Td>{`${batch.files_processed}/${batch.files_count}`}</Td>
                  <Td><StatusTag status={batch.status}>{batch.status}</StatusTag></Td>
                  <Td>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      {batch.status === 'processando' ? (
                        <IconButton onClick={() => apiExpenseManagement.cancelBatch(batch.batch_id)}>
                          <FontAwesomeIcon icon={faStop} />
                        </IconButton>
                      ) : (
                        <>
                          {/* <IconButton >
                            <FontAwesomeIcon icon={faEdit} />
                          </IconButton> */}
                          <IconButton danger onClick={() => handleDelete(batch.batch_id)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </MainCard>

      {createModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <Title style={{ fontSize: '18px' }}>Novo Lote</Title>
              <FontAwesomeIcon icon={faTimes} onClick={() => setCreateModalOpen(false)} style={{ cursor: 'pointer', color: '#999' }} />
            </div>
            <FormGroup>
              <label>Identificação do Lote</label>
              <Input
                value={formState.name}
                onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                placeholder="Ex: Faturas Janeiro"
              />
            </FormGroup>
            <FormGroup>
              <label>Documentos</label>
              <UploadArea onClick={() => document.getElementById('file-input').click()}>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => setFileList(prev => [...prev, ...Array.from(e.target.files)])}
                />
                <FontAwesomeIcon icon={faUpload} size="lg" color="#ff8f40" />
                <p>Arraste ou clique para selecionar</p>
              </UploadArea>
              <FileList>
                {fileList.map((file, idx) => (
                  <FileItem key={idx}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>{file.name}</span>
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={{ color: '#ff4d4f', cursor: 'pointer' }}
                      onClick={() => setFileList(fileList.filter((_, i) => i !== idx))}
                    />
                  </FileItem>
                ))}
              </FileList>
            </FormGroup>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <PrimaryButton style={{ flex: 1 }} onClick={handleCreateSubmit}>Salvar</PrimaryButton>
              <PrimaryButton style={{ flex: 1, backgroundColor: '#f5f5f5', color: '#666' }} onClick={() => setCreateModalOpen(false)}>Cancelar</PrimaryButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {detailsModalOpen && batchDetails && (
        <ModalOverlay>
          <ModalContent width="700px">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <Title style={{ fontSize: '18px' }}>Arquivos: {batchDetails.name}</Title>
              <FontAwesomeIcon icon={faTimes} onClick={() => setDetailsModalOpen(false)} style={{ cursor: 'pointer', color: '#999' }} />
            </div>
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <Th>Nome</Th>
                    <Th>Status</Th>
                    <Th style={{ textAlign: 'center' }}>Excluir</Th>
                  </tr>
                </thead>
                <tbody>
                  {batchFiles.map(file => (
                    <tr key={file.file_id}>
                      <Td>{file.name}</Td>
                      <Td><StatusTag status={file.status}>{file.status}</StatusTag></Td>
                      <Td style={{ textAlign: 'center' }}>
                        <IconButton danger onClick={() => apiExpenseManagement.deleteFileBatch(file.batch_id, file.file_id)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default BatchManagement;